import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ArrowRight, Scale, GraduationCap, Globe, MessageCircle,
  UserCheck, Target, Users, BookOpen, Headphones, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { courses, whyChooseData, faqData } from '../data/mock';
import { HeroSlider } from '../components/home/HeroSlider';
import { FacultySection } from '../components/home/FacultySection';
import { TestimonialsSlider } from '../components/home/TestimonialsSlider';
import { ResultsTrustSection, GalleryPreview, FinalCTA } from '../components/home/HomeSections';
import { toast } from 'sonner';
import { useState } from 'react';

const iconMap = {
  Scale, GraduationCap, Globe, MessageCircle,
  UserCheck, Target, Users, BookOpen, Headphones,
};

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ===================== QUICK ENQUIRY STRIP ===================== */
const QuickEnquiryStrip = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [course, setCourse] = useState('');

  const handleQuickEnquiry = (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !course) {
      toast.error('Please fill all fields');
      return;
    }
    if (!/^[0-9]{10}$/.test(phone.replace(/\s/g, ''))) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    const enquiries = JSON.parse(localStorage.getItem('scaleup_enquiries') || '[]');
    enquiries.push({ name, phone, course, email: '', message: '', timestamp: new Date().toISOString(), id: Date.now() });
    localStorage.setItem('scaleup_enquiries', JSON.stringify(enquiries));
    toast.success('Enquiry submitted! We will call you shortly.');
    setName(''); setPhone(''); setCourse('');
  };

  return (
    <section className="relative z-10 -mt-10 px-6">
      <div className="max-w-[1200px] mx-auto">
        <FadeInSection>
          <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6 md:p-8 shadow-2xl shadow-black/30">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="flex-shrink-0">
                <h3 className="text-white font-semibold text-lg">Quick Enquiry</h3>
                <p className="text-[rgb(161,161,170)] text-xs">Get a callback within 30 minutes</p>
              </div>
              <form onSubmit={handleQuickEnquiry} className="flex-1 flex flex-col sm:flex-row gap-3">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your Name"
                  className="bg-[rgb(38,40,42)] border-[rgb(63,63,63)] rounded-xl h-12 text-white placeholder:text-[rgb(161,161,170)] focus:border-[#F37021] focus:ring-1 focus:ring-[rgba(243,112,33,0.2)] flex-1"
                />
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone Number"
                  className="bg-[rgb(38,40,42)] border-[rgb(63,63,63)] rounded-xl h-12 text-white placeholder:text-[rgb(161,161,170)] focus:border-[#F37021] focus:ring-1 focus:ring-[rgba(243,112,33,0.2)] flex-1"
                />
                <Select value={course} onValueChange={setCourse}>
                  <SelectTrigger className="bg-[rgb(38,40,42)] border-[rgb(63,63,63)] rounded-xl h-12 text-white focus:border-[#F37021] focus:ring-1 focus:ring-[rgba(243,112,33,0.2)] flex-1 min-w-[160px]">
                    <SelectValue placeholder="Select Course" />
                  </SelectTrigger>
                  <SelectContent className="bg-[rgb(26,28,30)] border border-[rgb(63,63,63)]">
                    <SelectItem value="clat">CLAT</SelectItem>
                    <SelectItem value="cuet">CUET</SelectItem>
                    <SelectItem value="ielts">IELTS</SelectItem>
                    <SelectItem value="spoken-english">Spoken English</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="submit"
                  className="bg-[#F37021] hover:bg-[#DC5A14] text-white font-semibold rounded-xl h-12 px-8 border-0 transition-all duration-200 hover:shadow-[0_4px_15px_rgba(243,112,33,0.35)] whitespace-nowrap"
                >
                  Submit <Send size={16} className="ml-2" />
                </Button>
              </form>
            </div>
          </div>
        </FadeInSection>
      </div>
    </section>
  );
};

/* ===================== COURSES OVERVIEW ===================== */
const CoursesOverview = () => (
  <section className="py-24 bg-[rgb(17,17,19)] relative">
    <div className="max-w-[1200px] mx-auto px-6">
      <FadeInSection>
        <div className="text-center mb-16">
          <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">Our Programs</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">Courses We Offer</h2>
          <p className="text-[rgb(161,161,170)] text-lg max-w-2xl mx-auto">
            Expert-led programs designed to maximize your potential and deliver exceptional results.
          </p>
        </div>
      </FadeInSection>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {courses.map((course, i) => {
          const Icon = iconMap[course.icon] || Scale;
          return (
            <FadeInSection key={course.id} delay={i * 0.1}>
              <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 relative overflow-hidden group hover:-translate-y-1 hover:border-[#F37021] hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] transition-all duration-300">
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#F37021] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="flex items-start gap-5 mb-5">
                  <div className="w-14 h-14 rounded-2xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgba(243,112,33,0.15)] transition-colors duration-300">
                    <Icon size={24} className="text-[#F37021]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-[#F37021] transition-colors duration-200">{course.title}</h3>
                    <p className="text-[rgb(161,161,170)] text-sm leading-relaxed">{course.shortDesc}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-[rgb(218,218,218)] mb-5">
                  <span className="px-3 py-1.5 rounded-lg bg-[rgb(38,40,42)]">{course.duration}</span>
                  <span className="px-3 py-1.5 rounded-lg bg-[rgb(38,40,42)]">{course.mode}</span>
                  <span className="px-3 py-1.5 rounded-lg bg-[rgb(38,40,42)]">{course.batchSize}</span>
                </div>
                <div className="flex gap-3">
                  <Link to={`/courses#${course.id}`} className="flex-1">
                    <Button variant="outline" className="w-full border border-[rgb(63,63,63)] text-[rgb(218,218,218)] hover:border-[#F37021] hover:text-[#F37021] hover:bg-[rgba(243,112,33,0.06)] rounded-xl h-11 text-sm bg-transparent transition-all duration-200">
                      View Details
                    </Button>
                  </Link>
                  <Link to="/contact" className="flex-1">
                    <Button className="w-full bg-[#F37021] hover:bg-[#DC5A14] text-white font-semibold rounded-xl h-11 text-sm border-0 transition-all duration-200 hover:shadow-[0_4px_15px_rgba(243,112,33,0.35)]">
                      Enquire Now
                    </Button>
                  </Link>
                </div>
              </div>
            </FadeInSection>
          );
        })}
      </div>
    </div>
  </section>
);

/* ===================== WHY CHOOSE US ===================== */
const WhyChooseSection = () => (
  <section className="py-24 bg-[rgb(26,28,30)] relative">
    <div className="max-w-[1200px] mx-auto px-6">
      <FadeInSection>
        <div className="text-center mb-16">
          <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">Why Choose Us</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">The Scaleup Advantage</h2>
          <p className="text-[rgb(161,161,170)] text-lg max-w-2xl mx-auto">
            Everything you need for exam success, backed by expert guidance and cutting-edge technology.
          </p>
        </div>
      </FadeInSection>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {whyChooseData.map((item, i) => {
          const Icon = iconMap[item.icon] || Target;
          return (
            <FadeInSection key={i} delay={i * 0.08}>
              <div className="bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-7 group hover:-translate-y-1 hover:border-[rgba(243,112,33,0.35)] transition-all duration-300 h-full">
                <div className="w-12 h-12 rounded-xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center mb-5 group-hover:bg-[rgba(243,112,33,0.15)] transition-colors duration-300">
                  <Icon size={22} className="text-[#F37021]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[rgb(161,161,170)] text-sm leading-relaxed">{item.desc}</p>
              </div>
            </FadeInSection>
          );
        })}
      </div>
    </div>
  </section>
);

/* ===================== FAQ SECTION ===================== */
const FAQSection = () => (
  <section className="py-24 bg-[rgb(17,17,19)] relative">
    <div className="max-w-[800px] mx-auto px-6">
      <FadeInSection>
        <div className="text-center mb-16">
          <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">FAQs</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">Frequently Asked Questions</h2>
        </div>
      </FadeInSection>

      <FadeInSection delay={0.1}>
        <Accordion type="single" collapsible className="space-y-3">
          {faqData.map((faq, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl px-6 overflow-hidden data-[state=open]:border-[rgba(243,112,33,0.35)] transition-colors duration-200"
            >
              <AccordionTrigger className="text-white text-base font-medium hover:text-[#F37021] hover:no-underline py-5 [&[data-state=open]]:text-[#F37021]">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-[rgb(161,161,170)] text-sm leading-relaxed pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </FadeInSection>
    </div>
  </section>
);

/* ===================== HOME PAGE ===================== */
export default function HomePage() {
  return (
    <main>
      <HeroSlider />
      <QuickEnquiryStrip />
      <CoursesOverview />
      <WhyChooseSection />
      <FacultySection />
      <ResultsTrustSection />
      <TestimonialsSlider />
      <GalleryPreview />
      <FAQSection />
      <FinalCTA />
    </main>
  );
}
